import { useMutation, useQuery } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/styles';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentViewPropertySession } from 'src/hooks/use-current-view-property-session';
import { useViewer } from 'src/hooks/use-viewer';
import * as yup from 'yup';
import Button from '~components/button';
import FormikInput from '~components/input/formik-input';
import { FormikSelectWithActionOptions } from '~components/react-select';
import FormikSelect from '~components/select/formik-select';
import Typography from '~components/typography';
import { GET_LANGUAGES } from '~containers/email-templates/gql';
import { useReplaceSuiteStudentToCustomer } from '~containers/suite-config/suite-context';
import { CUSTOM_FIELD_MODEL_TYPE_OPTIONS, GENDER } from '~graphql-types';
import useDisplayResponseErrorToast from '~utils/use-display-response-error-toast';
import useDisplayToast from '~utils/use-display-toast';
import useHandleError from '~utils/use-handle-error';
import { usePersonDropdownOptions } from '~utils/use-person-dropdown-options';
import PropertySelect from './property-select';
import { CREATE_STUDENT_USER } from './step-find-student';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 560,
    paddingBottom: 40,
    ...theme.flex.colCenter,
  },
  formWrapper: {
    ...theme.flex.crossStart,
    flexDirection: 'column',
  },
  actions: {
    padding: '12px 16px 12px 0',
    justifyContent: 'flex-end',
  },
  content: {
    paddingTop: 32,
    paddingBottom: 32,
    paddingLeft: 64,
    display: 'grid',
    rowGap: '20px',
  },
  title: { marginTop: 18 },
  email: { marginTop: 8, marginBottom: 12 },
  field: { marginTop: 20, width: 320, height: 40 },
  createButton: { marginTop: 32, width: 320, height: 48 },
  createEnquiry: { marginTop: 24, width: 320 },
  tips: { marginTop: 8 },
  propertySelect: {
    margin: '8px 0 12px',
    cursor: 'pointer',
  },
}));

function StepNewStudent(props) {
  const { student, onStudentCreated, searchValue } = props;
  const displayResponseErrorToast = useDisplayResponseErrorToast();
  const [{ currentViewProperty }] = useCurrentViewPropertySession();
  const displayToast = useDisplayToast();
  const { t } = useTranslation('student');
  const { t: bookingT } = useTranslation('booking');
  const replaceSuiteStudentToCustomer = useReplaceSuiteStudentToCustomer();
  const {
    topLandlord: { id: topLandlordId },
  } = useViewer();

  const { heardSourceOptions } = usePersonDropdownOptions({
    variables: {
      topLandlordId,
      model: CUSTOM_FIELD_MODEL_TYPE_OPTIONS.STUDENT,
    },
  });

  const classes = useStyles();
  const formikRef = useRef(null);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [checkJumpPage, setCheckJumpPage] = useState(false);
  const [selectProperty, setSelectProperty] = useState(currentViewProperty);

  const emailLabel = t('student_details.general_information.email');
  const firstNameLabel = t('student_details.general_information.first_name');
  const lastNameLabel = t('student_details.general_information.last_name');

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('student_details.general_information.email.error'))
      .label(emailLabel)
      .required(),
    firstName: yup.string().label(firstNameLabel).required(),
    middleName: yup.string(),
    lastName: yup.string().label(lastNameLabel).required(),
    gender: yup.string(),
    heardSource: yup.string(),
    // preferrdLanguage: yup.string(),
  });

  const initialValues = useMemo(
    () => ({
      email: searchValue && searchValue.includes('@') ? searchValue : '',
      firstName: student?.firstName,
      middleName: student?.middleName,
      lastName: student?.lastName,
      gender: student?.gender,
      heardSource: student?.heardSource,
      // preferrdLanguage: data?.preferrdLanguage,
    }),
    [
      student?.firstName,
      student?.gender,
      student?.heardSource,
      student?.lastName,
      student?.middleName,
      searchValue,
    ]
  );

  const [createStudentUser, { error, loading }] = useMutation(CREATE_STUDENT_USER, {
    onCompleted: () => {
      const successText = replaceSuiteStudentToCustomer(
        bookingT(`booking.creating.${checkJumpPage ? 'enquiry' : 'student.new'}`),
        bookingT(`booking.creating.${checkJumpPage ? 'enquiry' : 'customer.new'}`)
      );

      displayToast('success-created', successText);
    },
  });

  if (error?.graphQLErrors[0]?.message === 'STUDENT_USER_ALREADY_EXISTS') {
    formikRef.current.setFieldError(
      'email',
      t('student_details.general_information.email.already_exist')
    );
  }
  useHandleError(error?.graphQLErrors[0]?.message === 'STUDENT_USER_ALREADY_EXISTS' ? null : error);

  const onSubmit = async (fields) => {
    try {
      const { data } = await createStudentUser({
        variables: {
          propertyId: selectProperty?.id,
          email: fields.email,
          firstName: fields.firstName,
          middleName: fields.middleName === '' ? undefined : fields.middleName,
          lastName: fields.lastName,
          gender: fields.gender || null,
          heardSource: fields.heardSource || undefined,
          preferredLanguage: fields.preferredLanguage?.value || undefined,
        },
      });
      if (onStudentCreated) onStudentCreated(data.createStudentUser);
    } catch (error) {
      displayResponseErrorToast(error);
    }
  };

  const handleSubmit = (e, formik) => {
    e.preventDefault();
    const checkType = e.type !== 'submit';
    if (checkJumpPage !== checkType) {
      setCheckJumpPage(checkType);
    }
    if (!formik.isSubmitting) {
      formik.handleSubmit();
    }
  };

  const handlePropertyChange = (e) => {
    setSelectProperty(e);
  };

  const queryLanguageResult = useQuery(GET_LANGUAGES, {
    fetchPolicy: 'cache-first',
  });
  useHandleError(queryLanguageResult.error);

  const languages = useMemo(() => {
    const filteredLanguages = queryLanguageResult.data?.getLanguages.languages?.filter(
      (lang) => lang?.abbreviation !== 'SC'
    );

    return filteredLanguages?.map((item) => {
      return {
        value: item.abbreviation,
        label: item.language,
      };
    });
  }, [queryLanguageResult.data?.getLanguages.languages]);

  return (
    <div className={classes.root}>
      <Typography variant="h2" themeColor="dark" align="center" className={classes.title}>
        {replaceSuiteStudentToCustomer(
          bookingT('booking.creating.student.new'),
          bookingT('booking.creating.customer.new')
        )}
      </Typography>

      <PropertySelect
        className={classes.propertySelect}
        property={selectProperty}
        onChange={handlePropertyChange}
        edit={false}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={shouldValidate}
        validateOnChange={shouldValidate}
        onSubmit={onSubmit}
      >
        {(formik) => {
          if (formik.submitCount > 0 && !shouldValidate) setShouldValidate(true);
          formikRef.current = formik;
          return (
            <form onSubmit={(e) => handleSubmit(e, formik)} className={classes.formWrapper}>
              <FormikInput
                autoFocus
                className={classes.field}
                placeholder={emailLabel}
                fieldConfig="email"
              />
              <FormikInput
                className={classes.field}
                placeholder={firstNameLabel}
                fieldConfig="firstName"
              />

              <FormikInput
                className={classes.field}
                placeholder={`${t('student_details.general_information.middle_name')} (${t(
                  'field.optional'
                )})`}
                fieldConfig="middleName"
              />

              <FormikInput
                className={classes.field}
                placeholder={lastNameLabel}
                fieldConfig="lastName"
              />

              <FormikSelect
                className={classes.field}
                placeholder={`${t('student_list.gender')} (${t('field.optional')})`}
                fieldConfig="gender"
                options={Object.values(GENDER).map((item) => ({
                  value: item,
                  children: t(`graphql:gender.${item}`),
                }))}
              />
              <FormikSelect
                className={classes.field}
                placeholder={`${t('student_details.heard_source')} (${t('field.optional')})`}
                fieldConfig="heardSource"
                options={heardSourceOptions.map((item) => ({
                  value: item.value,
                  children: t(`graphql:heard_source_type.${item.value.toUpperCase()}`),
                }))}
              />
              <FormikSelectWithActionOptions
                className={classes.field}
                fieldConfig="preferredLanguage"
                loading={queryLanguageResult.loading}
                options={languages}
                getOptionValue={(language) => language.value}
                getOptionLabel={(language) => language.label}
                placeholder={replaceSuiteStudentToCustomer(
                  t('student_details.preferred_language.placeholder'),
                  t('student_details.preferred_language.placeholder.CUSTOMER')
                )}
              />

              <Button
                type="submit"
                key="confirm"
                className={classes.createButton}
                color="primary"
                loading={loading}
              >
                {bookingT('booking.creating.student.create_booking')}
              </Button>
              <Button
                className={classes.createEnquiry}
                variant="text"
                onClick={(e) => handleSubmit(e, formik)}
                disabled={loading}
              >
                {bookingT('booking.creating.create_enquiry_only')}
              </Button>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

StepNewStudent.propTypes = {
  student: PropTypes.object,
  searchValue: PropTypes.string,
  onStudentCreated: PropTypes.func,
};

export default StepNewStudent;
